import { useState, useEffect } from 'react';
import api from '../../api';

const STATUS_CONFIG = {
  pending: { color: 'var(--warning)', label: 'Pending', emoji: '⏳' },
  approved: { color: 'var(--success)', label: 'Approved', emoji: '✅' },
  denied: { color: 'var(--danger)', label: 'Denied', emoji: '❌' },
};

const LOA_TYPE = {
  short_term: 'Short Term (1–2 weeks)',
  long_term: 'Long Term',
  return_to_duty: '🔄 Return to Duty',
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px',
      background: cfg.color + '20', color: cfg.color,
    }}>
      {cfg.emoji} {cfg.label}
    </span>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel, noteLabel }) {
  const [note, setNote] = useState('');
  return (
    <div onClick={onCancel} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: '16px', padding: '28px',
        maxWidth: '420px', width: '90%',
        border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)', marginBottom: '10px' }}>{title}</h3>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.5 }}>{message}</p>
        {noteLabel && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>{noteLabel}</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Optional note to send to the staff member..."
              style={{
                width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                border: '2px solid var(--border)', borderRadius: '10px',
                fontSize: '14px', fontFamily: 'var(--font-body)',
                background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
              }} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-secondary)',
          }}>Cancel</button>
          <button onClick={() => onConfirm(note)} style={{
            padding: '10px 20px', background: 'var(--gradient-accent)', border: 'none',
            borderRadius: '8px', cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: '14px', color: '#fff', fontWeight: 700,
          }}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

export default function RequestsPage({ guild }) {
  const [tab, setTab] = useState('loa');
  const [loaFilter, setLoaFilter] = useState('pending');
  const [adjFilter, setAdjFilter] = useState('pending');
  const [loa, setLoa] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [modal, setModal] = useState(null); // { type: 'loa_approve'|'loa_deny'|'adj_approve'|'adj_deny', id }

  const loadData = async () => {
    setLoading(true);
    try {
      const [loaRes, adjRes] = await Promise.all([
        api.get(`/api/loa/${guild.id}?status=all`),
        api.get(`/api/adjustments/${guild.id}?status=all`),
      ]);
      setLoa(loaRes.data);
      setAdjustments(adjRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [guild.id]);

  const handleLoaAction = async (requestId, action, note) => {
    setActioning(requestId);
    setModal(null);
    try {
      await api.post(`/api/loa/${guild.id}/${requestId}/${action}`, { admin_note: note });
      await loadData();
    } catch { alert(`Failed to ${action} request.`); }
    finally { setActioning(null); }
  };

  const handleAdjAction = async (adjId, action) => {
    setActioning(adjId);
    setModal(null);
    try {
      await api.patch(`/api/adjustments/${guild.id}/${adjId}`, { status: action });
      await loadData();
    } catch { alert(`Failed to ${action} adjustment.`); }
    finally { setActioning(null); }
  };

  const filteredLoa = loa.filter(r => loaFilter === 'all' || r.status === loaFilter);
  const filteredAdj = adjustments.filter(r => adjFilter === 'all' || r.status === adjFilter);
  const pendingLoa = loa.filter(r => r.status === 'pending').length;
  const pendingAdj = adjustments.filter(r => r.status === 'pending').length;

  const inputStyle = {
    padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px',
    fontSize: '13px', fontFamily: 'var(--font-body)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading requests...
    </div>
  );

  return (
    <div>
      {modal?.type === 'loa_approve' && (
        <ConfirmModal
          title="Approve LOA Request"
          message={`Approving will set this staff member's status to On Leave and send them a DM notification.`}
          noteLabel="Admin note (optional)"
          onConfirm={note => handleLoaAction(modal.id, 'approve', note)}
          onCancel={() => setModal(null)}
        />
      )}
      {modal?.type === 'loa_deny' && (
        <ConfirmModal
          title="Deny LOA Request"
          message="The staff member will be notified of the denial via DM."
          noteLabel="Reason for denial (optional)"
          onConfirm={note => handleLoaAction(modal.id, 'deny', note)}
          onCancel={() => setModal(null)}
        />
      )}

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Requests</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
          {pendingLoa + pendingAdj} pending — {pendingLoa} LOA, {pendingAdj} time adjustments
        </p>
      </div>

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-secondary)', borderRadius: '12px', padding: '4px', width: 'fit-content' }}>
        {[
          { key: 'loa', label: `📋 LOA & RTR${pendingLoa > 0 ? ` (${pendingLoa})` : ''}` },
          { key: 'adjustments', label: `⚖️ Time Adjustments${pendingAdj > 0 ? ` (${pendingAdj})` : ''}` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 18px', borderRadius: '8px', cursor: 'pointer',
            border: 'none', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
            background: tab === t.key ? 'var(--bg-card)' : 'transparent',
            color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: tab === t.key ? 'var(--shadow-card)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {/* LOA Tab */}
      {tab === 'loa' && (
        <div>
          {/* Filter */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
            {['pending', 'approved', 'denied', 'all'].map(s => (
              <button key={s} onClick={() => setLoaFilter(s)} style={{
                padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', border: 'none',
                fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600,
                background: loaFilter === s ? 'var(--bg-card)' : 'transparent',
                color: loaFilter === s ? 'var(--text-primary)' : 'var(--text-muted)',
                boxShadow: loaFilter === s ? 'var(--shadow-card)' : 'none',
                textTransform: 'capitalize',
              }}>{s}</button>
            ))}
          </div>

          {filteredLoa.length === 0 ? (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              No {loaFilter === 'all' ? '' : loaFilter} LOA requests.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredLoa.map(req => (
                <div key={req.id} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: '16px', padding: '20px 24px',
                  boxShadow: 'var(--shadow-card)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                        background: 'var(--gradient-accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '15px', fontWeight: 700, color: '#fff',
                      }}>
                        {(req.display_name || req.username || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {req.display_name || req.username}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {new Date(req.requested_at).toLocaleString()} · Request #{req.id}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={req.status} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '3px' }}>Type</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{LOA_TYPE[req.type] || req.type}</div>
                    </div>
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '3px' }}>Duration</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{req.duration || '—'}</div>
                    </div>
                  </div>

                  <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '12px', marginBottom: req.status === 'pending' ? '14px' : '0' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Reason</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.5 }}>{req.reason}</div>
                  </div>

                  {req.admin_note && (
                    <div style={{ background: 'rgba(52,152,219,0.08)', border: '1px solid rgba(52,152,219,0.2)', borderRadius: '8px', padding: '10px 12px', marginTop: '10px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--info)', marginBottom: '3px' }}>Admin Note</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{req.admin_note}</div>
                    </div>
                  )}

                  {req.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                      <button
                        onClick={() => setModal({ type: 'loa_approve', id: req.id })}
                        disabled={actioning === req.id}
                        style={{
                          padding: '10px 20px', background: 'var(--success)', color: '#fff',
                          border: 'none', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: 700, fontFamily: 'var(--font-display)',
                          opacity: actioning === req.id ? 0.7 : 1,
                        }}>
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => setModal({ type: 'loa_deny', id: req.id })}
                        disabled={actioning === req.id}
                        style={{
                          padding: '10px 20px', background: 'transparent', color: 'var(--danger)',
                          border: '1px solid var(--danger)', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: 700, fontFamily: 'var(--font-body)',
                        }}>
                        ❌ Deny
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Time Adjustments Tab */}
      {tab === 'adjustments' && (
        <div>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
            {['pending', 'approved', 'denied', 'all'].map(s => (
              <button key={s} onClick={() => setAdjFilter(s)} style={{
                padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', border: 'none',
                fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600,
                background: adjFilter === s ? 'var(--bg-card)' : 'transparent',
                color: adjFilter === s ? 'var(--text-primary)' : 'var(--text-muted)',
                boxShadow: adjFilter === s ? 'var(--shadow-card)' : 'none',
                textTransform: 'capitalize',
              }}>{s}</button>
            ))}
          </div>

          {filteredAdj.length === 0 ? (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚖️</div>
              No {adjFilter === 'all' ? '' : adjFilter} time adjustment requests.
            </div>
          ) : (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr',
                padding: '12px 20px', borderBottom: '1px solid var(--border)',
                fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: 'var(--text-muted)',
              }}>
                <span>Staff Member</span><span>Direction</span><span>Amount</span><span>Reason</span><span>Action</span>
              </div>

              {filteredAdj.map(adj => (
                <div key={adj.id} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr',
                  padding: '14px 20px', borderBottom: '1px solid var(--border)',
                  alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {adj.display_name || adj.username}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {new Date(adj.requested_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: adj.direction === '+' ? 'var(--success)' : 'var(--danger)' }}>
                    {adj.direction === '+' ? '+ Add' : '− Remove'}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                    {adj.hours}h {adj.minutes > 0 ? `${adj.minutes}m` : ''}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adj.reason}
                  </div>
                  <div>
                    {adj.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleAdjAction(adj.id, 'approved')} disabled={actioning === adj.id}
                          style={{ padding: '6px 12px', background: 'var(--success)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
                          ✅
                        </button>
                        <button onClick={() => handleAdjAction(adj.id, 'denied')} disabled={actioning === adj.id}
                          style={{ padding: '6px 12px', background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          ❌
                        </button>
                      </div>
                    ) : (
                      <StatusBadge status={adj.status} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
